import React from 'react';
import { Box, Paper, Stack, Typography, alpha } from '@mui/material';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import LightbulbRounded from '@mui/icons-material/LightbulbRounded';
import type { ChainRootCausePanel } from './types';

export interface RootCausePanelProps {
  panel: ChainRootCausePanel;
  /** Optional extra content rendered below the standard fields — e.g. a
   * "view cross-broker pivot" action button, specific to the consuming page. */
  actions?: React.ReactNode;
}

/** Generic "what happened / why / what to do next" drill-down panel.
 * Renders whatever deterministic narrative the backend computed — this
 * component has no domain knowledge of what "cause" values mean. */
export default function RootCausePanel({ panel, actions }: RootCausePanelProps) {
  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        overflow: 'hidden',
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={(t) => ({ px: 2, py: 1.25, borderBottom: `1px solid ${t.tokens.border.subtle}`, bgcolor: t.tokens.surface.sunken })}
      >
        <InsightsRounded sx={{ fontSize: 18, color: 'info.main' }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {panel.entity_name}
        </Typography>
      </Stack>
      <Box sx={{ p: 2.5 }}>
        <Field label="What happened" text={panel.what_happened} />
        <Field label="Why" text={panel.why} />
        <Box
          sx={(t) => ({
            mt: 1.5,
            p: 1.5,
            borderRadius: `${t.ds?.radius?.sm ?? 6}px`,
            bgcolor: alpha(t.palette.warning.main, 0.06),
            border: `1px solid ${alpha(t.palette.warning.main, 0.2)}`,
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
          })}
        >
          <LightbulbRounded sx={{ fontSize: 18, color: 'warning.main', mt: 0.1, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            {panel.suggestion}
          </Typography>
        </Box>
        {actions && <Box sx={{ mt: 1.5 }}>{actions}</Box>}
        {panel.method_note && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}>
            {panel.method_note}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function Field({ label, text }: { label: string; text: string }) {
  if (!text) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 0.25 }}>
        {text}
      </Typography>
    </Box>
  );
}
